import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LoanActions, SettingsActions } from '@portal/actions';

import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';

import { getRouteStackPath } from '@portal/config/routes';
import useReduxState from '@portal/hooks/useReduxState';
import { Gender } from '@portal/models/enumerators/loans';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { removeAccent } from '@portal/services/masks';
import * as MessageService from '@portal/services/message';
import { useNavigation } from '@portal/hooks/useNavigation';

const { Option } = Select;

const LoanRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const [disabled, setDisabled] = useState(true);
  const [gender, setGender] = useState<Gender | null>(null);
  const [selectedBusinessLine, setSelectedBusinessLine] = useState<
    number | null
  >(null);
  const [financialStatistic, setFinancialStatistic] = useState<number | null>(
    null
  );

  const {
    settings: { settings, loanSettings },
    loan: { success, error },
    user: {
      me: {
        account: { type },
      },
    },
  } = useReduxState();

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    }
    if (!loanSettings && type === AccountTaxIdentifierType.CORPORATE) {
      dispatch(SettingsActions.getLoanSettings());
    }
  }, [dispatch, loanSettings, settings, type]);

  useEffect(() => {
    if (error) {
      MessageService.error(error.message, { originalError: error });
      dispatch(LoanActions.setLoanError(null));
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (success) {
      MessageService.success(`PAGES.LOAN.SUCCESS_MESSAGES.${success}`);
      dispatch(LoanActions.setLoanSuccess(null));
      navigate(getRouteStackPath('HOME', 'HOME_PAGE'));
    }
  }, [dispatch, navigate, success]);

  useEffect(() => {
    if (type === AccountTaxIdentifierType.PERSON) {
      if (gender && selectedBusinessLine) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    } else {
      if (financialStatistic) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [financialStatistic, gender, selectedBusinessLine, type]);

  const handleAdvance = () => {
    if (type === AccountTaxIdentifierType.PERSON) {
      if (gender && selectedBusinessLine) {
        dispatch(
          LoanActions.updateAccount({
            gender,
            businessLine: selectedBusinessLine,
          })
        );
      }
    } else {
      if (financialStatistic) {
        dispatch(LoanActions.updateAccount({ financialStatistic }));
      }
    }
  };

  return (
    <div className="loan-register">
      <PanelContentHeader
        pageTitle={translate('PAGES.LOAN.REGISTER.TITLE')}
        pageDescription={translate('PAGES.LOAN.REGISTER.SUBTITLE')}
      />
      {type === AccountTaxIdentifierType.PERSON && (
        <div className="loan-register__inputs">
          <label className="loan-register__inputs__label">
            {translate('PAGES.LOAN.REGISTER.GENDERS.TITLE')}
          </label>
          <Select
            className="loan-register__inputs__select"
            showSearch
            allowClear
            bordered={false}
            placeholder={translate('PAGES.LOAN.REGISTER.GENDERS.PLACEHOLDER')}
            filterOption={(input, option) =>
              removeAccent(option?.children ? option.children.toString() : '')
                .toLowerCase()
                .indexOf(removeAccent(input).toLowerCase()) >= 0
            }
            value={gender}
            onChange={(value) => setGender(value ?? null)}
          >
            <Option value={Gender.MALE}>
              {translate('PAGES.LOAN.REGISTER.GENDERS.MALE')}
            </Option>
            <Option value={Gender.FEMALE}>
              {translate('PAGES.LOAN.REGISTER.GENDERS.FEMALE')}
            </Option>
            <Option value={Gender.OTHER}>
              {translate('PAGES.LOAN.REGISTER.GENDERS.OTHER')}
            </Option>
            <Option value={Gender.NOT_SPECIFIED}>
              {translate('PAGES.LOAN.REGISTER.GENDERS.NOT_SPECIFIED')}
            </Option>
          </Select>
        </div>
      )}
      {settings && type === AccountTaxIdentifierType.PERSON ? (
        <div className="loan-register__inputs">
          <label className="loan-register__inputs__label">
            {translate('PAGES.LOAN.REGISTER.BUSINESS_LINES.TITLE')}
          </label>
          <Select
            className="loan-register__inputs__select"
            showSearch
            allowClear
            bordered={false}
            placeholder={translate(
              'PAGES.LOAN.REGISTER.BUSINESS_LINES.PLACEHOLDER'
            )}
            filterOption={(input, option) =>
              removeAccent(option?.children ? option.children.toString() : '')
                .toLowerCase()
                .indexOf(removeAccent(input).toLowerCase()) >= 0
            }
            value={selectedBusinessLine}
            onChange={(value) => setSelectedBusinessLine(value ?? null)}
          >
            {settings.businessLine.map((businessLine) => (
              <Option key={businessLine.id} value={businessLine.id}>
                {businessLine.type}
              </Option>
            ))}
          </Select>
        </div>
      ) : null}
      {loanSettings && type === AccountTaxIdentifierType.CORPORATE ? (
        <div className="loan-register__inputs">
          <label className="loan-register__inputs__label">
            {translate('PAGES.LOAN.REGISTER.FINANCIAL_STATISTIC.TITLE')}
          </label>
          <Select
            className="loan-register__inputs__select"
            showSearch
            allowClear
            bordered={false}
            placeholder={translate(
              'PAGES.LOAN.REGISTER.FINANCIAL_STATISTIC.PLACEHOLDER'
            )}
            filterOption={(input, option) =>
              removeAccent(option?.children ? option.children.toString() : '')
                .toLowerCase()
                .indexOf(removeAccent(input).toLowerCase()) >= 0
            }
            value={financialStatistic}
            onChange={(value) => setFinancialStatistic(value ?? null)}
          >
            {loanSettings.financialStatistic.map((financialStatistic) => (
              <Option key={financialStatistic.id} value={financialStatistic.id}>
                {financialStatistic.type}
              </Option>
            ))}
          </Select>
        </div>
      ) : null}
      <div className="loan-register__buttons">
        <AdvancedButton
          text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
          onClick={handleAdvance}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default LoanRegister;
